import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "../App";

function Runner({ startHint, gameOver, restart, controlsLabel, runnerLabel }: { startHint: string; gameOver: string; restart: string; controlsLabel: string; runnerLabel: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const GROUND_Y = H - 28;
    const GRAVITY = 0.6;
    const JUMP_V = -11;

    const player = { x: 48, y: GROUND_Y - 28, w: 22, h: 28, vy: 0, onGround: true };
    let obstacles: Array<{ x: number; w: number; h: number }> = [];
    let speed = 5;
    let frame = 0;
    let localScore = 0;
    let over = false;
    let running = true;
    let started = false;

    const spawn = () => {
      const h = 18 + Math.random() * 22;
      const w = 10 + Math.random() * 14;
      obstacles.push({ x: W + 20, w, h });
    };

    const reset = () => {
      player.y = GROUND_Y - player.h;
      player.vy = 0;
      player.onGround = true;
      obstacles = [];
      speed = 5;
      frame = 0;
      localScore = 0;
      over = false;
      setScore(0);
    };

    const jump = () => {
      if (!started) { started = true; }
      if (over) { reset(); return; }
      if (player.onGround) { player.vy = JUMP_V; player.onGround = false; }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.code === "ArrowUp") { e.preventDefault(); jump(); }
    };
    const onClick = () => jump();

    window.addEventListener("keydown", onKey);
    canvas.addEventListener("mousedown", onClick);
    canvas.addEventListener("touchstart", (e) => { e.preventDefault(); jump(); }, { passive: false });

    const loop = () => {
      if (!running) return;
      ctx.clearRect(0, 0, W, H);

      ctx.strokeStyle = "#2a2a2a";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y + 0.5);
      ctx.lineTo(W, GROUND_Y + 0.5);
      ctx.stroke();

      ctx.fillStyle = "#3a3a3a";
      for (let i = 0; i < W; i += 24) {
        const x = (i - (frame * speed) % 24);
        ctx.fillRect(x, GROUND_Y + 4, 2, 2);
      }

      if (started && !over) {
        player.vy += GRAVITY;
        player.y += player.vy;
        if (player.y >= GROUND_Y - player.h) { player.y = GROUND_Y - player.h; player.vy = 0; player.onGround = true; }

        if (frame % Math.max(60, 110 - Math.floor(speed * 4)) === 0 && Math.random() > 0.3) { spawn(); }
        obstacles.forEach(o => (o.x -= speed));
        obstacles = obstacles.filter(o => o.x + o.w > -10);

        for (const o of obstacles) {
          const ox = o.x, oy = GROUND_Y - o.h, ow = o.w, oh = o.h;
          if (player.x < ox + ow && player.x + player.w > ox && player.y < oy + oh && player.y + player.h > oy) {
            over = true;
            setBest(b => {
              const nb = Math.max(b, localScore);
              try { localStorage.setItem("404-best", String(nb)); } catch {}
              return nb;
            });
          }
        }

        frame++;
        if (frame % 6 === 0) { localScore++; setScore(localScore); }
        speed = 5 + Math.min(6, localScore / 80);
      }

      ctx.fillStyle = "#FF3C00";
      for (const o of obstacles) { ctx.fillRect(o.x, GROUND_Y - o.h, o.w, o.h); }

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(player.x, player.y, player.w, player.h);
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(player.x + player.w - 6, player.y + 5, 3, 3);

      if (!started) {
        ctx.fillStyle = "#888";
        ctx.font = "12px ui-monospace, monospace";
        ctx.fillText(startHint, W / 2 - 150, H / 2);
      }

      if (over) {
        ctx.fillStyle = "#fff";
        ctx.font = "bold 16px ui-monospace, monospace";
        ctx.fillText(gameOver, W / 2 - 45, H / 2 - 6);
        ctx.font = "11px ui-monospace, monospace";
        ctx.fillStyle = "#888";
        ctx.fillText(restart, W / 2 - 115, H / 2 + 12);
      }

      requestAnimationFrame(loop);
    };

    try {
      const b = Number(localStorage.getItem("404-best") || "0");
      if (b) setBest(b);
    } catch {}

    loop();

    return () => {
      running = false;
      window.removeEventListener("keydown", onKey);
      canvas.removeEventListener("mousedown", onClick);
    };
  }, [startHint, gameOver, restart]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3 font-mono text-xs text-white/40 px-5 sm:px-8 md:px-[66px]">
        <span className="uppercase tracking-widest">{runnerLabel}</span>
        <div className="flex gap-4">
          <span>HI {String(best).padStart(5, "0")}</span>
          <span className="text-white">{String(score).padStart(5, "0")}</span>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        width={900}
        height={240}
        className="w-full bg-transparent cursor-pointer select-none block"
        style={{ imageRendering: "pixelated" }}
      />
      <p className="text-[11px] text-white/25 mt-2 font-mono px-5 sm:px-8 md:px-[66px]">{controlsLabel}</p>
    </div>
  );
}

export function NotFound() {
  const { t } = useTranslation("notFound");
  const { t: tc } = useTranslation("common");
  const titles = t("titles", { returnObjects: true }) as string[];
  const [title] = useState(() => titles[Math.floor(Math.random() * titles.length)]!);

  return (
    <div data-subpage className="min-h-screen md:h-screen md:min-h-0 flex flex-col">
      <div className="px-5 sm:px-8 md:px-[66px] pt-28 sm:pt-32 md:pt-28">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#FF3C00]/80 font-mono mb-4">{t("error")}</div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-[-0.03em] leading-[1.02] mb-8 whitespace-pre-line">{title}</h1>
      </div>

      <div className="flex-1 flex items-center w-full">
        <div className="w-full">
          <Runner
            startHint={t("startHint")}
            gameOver={t("gameOver")}
            restart={t("restart")}
            controlsLabel={t("controls")}
            runnerLabel={t("runnerLabel")}
          />
        </div>
      </div>

      <div className="px-5 sm:px-8 md:px-[66px] pb-10 md:pb-14">
        <div className="border-t border-grey-800 pt-6 flex items-center justify-between gap-6">
          <p className="text-xs text-white/40 leading-relaxed max-w-xs">{t("stayNote")}</p>
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate("/"); }}
            className="inline-flex items-center gap-2 text-white text-sm font-medium px-6 py-2.5 rounded-full transition-colors shrink-0"
            style={{ backgroundColor: "#FF3C00" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e63600")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#FF3C00")}
          >
            {tc("backHome")}
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}
